/* eslint-disable @typescript-eslint/no-unused-vars */
/* global kakao */
import * as React from 'react';
import axios from 'axios';
import { Grid, FormHelperText, Typography } from '@mui/material';
import {
  Map,
  Polygon,
  DrawingManager,
  CustomOverlayMap,
} from 'react-kakao-maps-sdk';
import { EditToggleButton, IconLabelButton } from '../territorycard/Buttons';
import { AlertDialog } from '../utils/AlertDialog';
import config from '../../config';
import { KakaoAddress, LatLng, Layer } from '../../types';

function DrawingManagerWrapper(props: any) {
  const { managerRef } = props;
  return (
    <DrawingManager
      ref={managerRef}
      drawingMode={[
        kakao.maps.drawing.OverlayType.ARROW,
        kakao.maps.drawing.OverlayType.CIRCLE,
        kakao.maps.drawing.OverlayType.ELLIPSE,
        kakao.maps.drawing.OverlayType.MARKER,
        kakao.maps.drawing.OverlayType.POLYLINE,
        kakao.maps.drawing.OverlayType.RECTANGLE,
        kakao.maps.drawing.OverlayType.POLYGON,
      ]}
      guideTooltip={['draw', 'drag', 'edit']}
      markerOptions={{
        // 마커 옵션입니다
        draggable: true, // 마커를 그리고 나서 드래그 가능하게 합니다
        removable: true, // 마커를 삭제 할 수 있도록 x 버튼이 표시됩니다
      }}
      polylineOptions={{
        // 선 옵션입니다
        draggable: true, // 그린 후 드래그가 가능하도록 설정합니다
        removable: true, // 그린 후 삭제 할 수 있도록 x 버튼이 표시됩니다
        editable: true, // 그린 후 수정할 수 있도록 설정합니다
        strokeColor: '#39f', // 선 색
        hintStrokeStyle: 'dash', // 그리중 마우스를 따라다니는 보조선의 선 스타일
        hintStrokeOpacity: 0.5, // 그리중 마우스를 따라다니는 보조선의 투명도
      }}
      rectangleOptions={{
        draggable: true,
        removable: true,
        editable: true,
        strokeColor: '#39f', // 외곽선 색
        fillColor: '#39f', // 채우기 색
        fillOpacity: 0.5, // 채우기색 투명도
      }}
      circleOptions={{
        draggable: true,
        removable: true,
        editable: true,
        strokeColor: '#39f',
        fillColor: '#39f',
        fillOpacity: 0.5,
      }}
      polygonOptions={{
        draggable: true,
        removable: true,
        editable: true,
        strokeColor: '#39f',
        fillColor: '#39f',
        fillOpacity: 0.5,
        hintStrokeStyle: 'dash',
        hintStrokeOpacity: 0.5,
      }}
      arrowOptions={{
        draggable: true, // 그린 후 드래그가 가능하도록 설정합니다
        removable: true, // 그린 후 삭제 할 수 있도록 x 버튼이 표시됩니다
        editable: true, // 그린 후 수정할 수 있도록 설정합니다
        strokeColor: '#39f', // 선 색
        hintStrokeStyle: 'dash', // 그리중 마우스를 따라다니는 보조선의 선 스타일
        hintStrokeOpacity: 0.5, // 그리중 마우스를 따라다니는 보조선의 투명도
      }}
      ellipseOptions={{
        draggable: true,
        removable: true,
        editable: true,
        strokeColor: '#39f',
        fillColor: '#39f',
        fillOpacity: 0.5,
      }}
    />
  );
}

export class ApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export const getCentoidOfPolygonPath = (polygonPath: LatLng[]) => {
  return polygonPath.reduce(
    (prevPoint: LatLng, currPoint: LatLng) => {
      return {
        lat: prevPoint.lat + currPoint.lat / polygonPath.length,
        lng: prevPoint.lng + currPoint.lng / polygonPath.length,
      };
    },
    { lat: 0, lng: 0 },
  );
};

export const getCentroidOfMap = (layers: Layer[]): LatLng => {
  if (layers.length > 0) {
    return getCentoidOfPolygonPath(
      layers.map((layer: Layer) => {
        return layer.centroid;
      }),
    );
  }
  // somewhere representative in Korea: Mokdong
  // set this as a env parameter
  return {
    lat: config.map.defaultCentroid.lat,
    lng: config.map.defaultCentroid.lng,
  };
};

export interface LayerEditableMapProps {
  editable: boolean;
  mapLevel: number;
  parent: Layer;
  layerLevel: number;
  layers: Layer[];
  setLayers: React.Dispatch<React.SetStateAction<Layer[]>>;
  center: LatLng;
  handleCenterChanged: (center: LatLng) => void;
  maxNumLayers?: number;
  layerClicked?: (layer: Layer) => void;
  onCurrentAddrChanged?: (addr: KakaoAddress) => void;
  apiEndpoint?: string;
}

export function LayerEditableMap({
  editable,
  mapLevel,
  parent,
  layerLevel,
  layers,
  setLayers,
  center,
  handleCenterChanged,
  maxNumLayers = config.map.defaultMaximumNumberOfLayers,
  layerClicked = (layer: Layer) => {},
  onCurrentAddrChanged = (addr: KakaoAddress) => {},
  apiEndpoint = undefined,
}: LayerEditableMapProps) {
  const [isLock, setIsLock] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState({
    status: false,
    message: '',
  });
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selected, setSelected] = React.useState<Layer>({} as Layer);
  // const [parent, setParent] = React.useState<Layer>({} as Layer);
  // const [center, setCenter] = React.useState<LatLng>({
  //   lat: config.map.defaultCentroid.lat,
  //   lng: config.map.defaultCentroid.lng,
  // });
  const [mouseOverStates, setMouseOverStates] = React.useState(
    Object.assign({}, ...layers.map(x => ({ [x.id]: false }))),
  );

  // React.useEffect(() => {
  //   handleCenter();
  // }, []);

  const managerRef =
    React.useRef<
      kakao.maps.drawing.DrawingManager<
        | kakao.maps.drawing.OverlayType.ARROW
        | kakao.maps.drawing.OverlayType.CIRCLE
        | kakao.maps.drawing.OverlayType.ELLIPSE
        | kakao.maps.drawing.OverlayType.MARKER
        | kakao.maps.drawing.OverlayType.POLYLINE
        | kakao.maps.drawing.OverlayType.RECTANGLE
        | kakao.maps.drawing.OverlayType.POLYGON
      >
    >(null);

  const selectOverlay = (type: kakao.maps.drawing.OverlayType) => () => {
    const manager = managerRef.current;
    manager?.cancel();
    manager?.select(type);
  };

  const handleEditToggle = () => {
    // check if polygon is in manager
    const overlays = managerRef.current?.getOverlays([
      kakao.maps.drawing.OverlayType.POLYGON,
    ]);
    if (overlays?.polygon?.length) {
      setErrorMessage({
        status: true,
        message: 'Confirm current layer first!',
      });
      return;
    }
    setErrorMessage({
      status: false,
      message: '',
    });

    // update layers through API
    // saveLayers(layers);
    setIsLock(!isLock);
  };

  const saveNewLayer = async () => {
    const manager = managerRef.current;
    const data = manager?.getData([kakao.maps.drawing.OverlayType.POLYGON]);
    const overlays = manager?.getOverlays([
      kakao.maps.drawing.OverlayType.POLYGON,
    ]);

    if (data?.polygon.length === 0) {
      return;
    }

    if (data?.polygon.length !== 1) {
      setErrorMessage({
        status: false,
        message: '새로운 구역은 한번에 하나만 그려주세요',
      });
      return;
    }
    setErrorMessage({
      status: false,
      message: '',
    });

    const polygonPath = data.polygon[0].points.map(({ x, y }, index) => {
      return {
        lat: y,
        lng: x,
      };
    });

    try {
      if (selected.id === undefined) {
        const newLayer = {
          level: layerLevel,
          name: '',
          description: '',
          polygonPath,
          centroid: getCentoidOfPolygonPath(polygonPath),
          parentName: Object.keys(parent).length === 0 ? '' : parent.name,
        };
        const { data } = await axios.post(
          `${config.api.url}/mdsouth/layers`,
          newLayer,
        );

        setLayers([
          ...layers,
          {
            id: data.id,
            ...newLayer,
          },
        ]);
      } else {
        // edit case
        const updatedLayer = {
          ...selected,
          polygonPath,
          centroid: getCentoidOfPolygonPath(polygonPath),
        };
        await axios.put(
          `${config.api.url}/mdsouth/layers/${selected.id}`,
          updatedLayer,
        );

        setLayers([...layers, updatedLayer]);
      }

      // remove user-drawn polygon from manager
      // need to use getOverlays()
      overlays?.polygon.forEach(elem => {
        manager?.remove(elem);
      });
    } catch (error) {
      console.log(error);
    }
    setSelected({} as Layer);
  };

  const handlMouseOver = (id: number) => {
    setMouseOverStates((prev: any) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleClicked = (id: number) => {
    const layer = layers.find(layer => layer.id === id);
    if (layer) {
      if (isLock) {
        layerClicked?.(layer);
      } else {
        // delete this clicked layer
        setSelected(layer);
        setOpenDialog(true);
      }
    }
  };

  const putLayerToManager = (layer: Layer) => {
    const manager = managerRef.current;
    if (manager) {
      manager.put(
        kakao.maps.drawing.OverlayType.POLYGON,
        layer.polygonPath.map((point: LatLng) => {
          return new kakao.maps.LatLng(point.lat, point.lng);
        }),
      );
    }
  };

  const handleDialogClose = (answer: string) => async () => {
    setLayers(
      layers.filter((layer: Layer) => {
        return layer.id !== selected.id;
      }),
    );
    if (answer === 'delete') {
      try {
        await axios.delete(`${config.api.url}/mdsouth/layers/${selected.id}`);
      } catch (error) {
        console.log(error);
      }
    } else if (answer === 'edit') {
      // add the selected to manager
      putLayerToManager(selected);
    }

    setOpenDialog(false);
  };

  const handleCenter = () => {
    handleCenterChanged(getCentroidOfMap(layers));
  };

  const getGeoLocation = () =>
    new Promise((resolve, error) =>
      // eslint-disable-next-line no-promise-executor-return
      navigator.geolocation.getCurrentPosition(resolve, error, {
        enableHighAccuracy: false,
        timeout: Infinity,
        maximumAge: Infinity,
      }),
    );

  const handleWhereAmI = async () => {
    if (navigator.geolocation) {
      const position: any = await getGeoLocation();
      handleCenterChanged({
        lat: position?.coords.latitude,
        lng: position?.coords.longitude,
      });
    } else {
      setErrorMessage({
        status: true,
        message: 'Geolocation is not supported on this device',
      });
    }
  };

  const onCenterChanged = (map: kakao.maps.Map) => {
    handleCenterChanged({
      lat: map.getCenter().getLat(),
      lng: map.getCenter().getLng(),
    });
  };

  const geocoder = new kakao.maps.services.Geocoder();

  const onMapClick = (
    target: kakao.maps.Map,
    mouseEvent: kakao.maps.event.MouseEvent,
  ) => {
    geocoder.coord2Address(
      mouseEvent.latLng.getLng(),
      mouseEvent.latLng.getLat(),
      (result, status) => {
        if (status === kakao.maps.services.Status.OK) {
          onCurrentAddrChanged(result[0]);
        }
      },
    );
  };

  return (
    <>
      <Map
        center={center}
        style={{
          // 지도의 크기
          width: '100%',
          height: '450px',
        }}
        level={mapLevel} // 지도의 확대 레벨
        onCenterChanged={onCenterChanged}
        onClick={onMapClick}
      >
        <DrawingManagerWrapper managerRef={managerRef} />
        {layers.map((layer: Layer, index) => (
          <Polygon
            key={layer.id}
            path={layer.polygonPath}
            strokeWeight={2} // 선의 두께입니다
            // strokeColor="#39DE2A" // 선의 색깔입니다
            strokeColor="#1976d2" // 선의 색깔입니다
            strokeOpacity={0.8} // 선의 불투명도입니다 0에서 1 사이값이며 0에 가까울수록 투명합니다
            strokeStyle="solid" // 선의 스타일입니다
            fillColor={mouseOverStates[layer.id] ? '#64b5f6' : '#90caf9'} // 채우기 색깔입니다
            fillOpacity={mouseOverStates[layer.id] ? 0.2 : 0.7} // 채우기 불투명도입니다
            onMouseover={() => {
              handlMouseOver(layer.id);
            }}
            onMouseout={() => {
              handlMouseOver(layer.id);
            }}
            onMousedown={() => {
              handleClicked(layer.id);
            }}
          />
        ))}
        {Object.keys(parent).length !== 0 ? (
          <Polygon
            path={parent.polygonPath}
            strokeWeight={3} // 선의 두께입니다
            strokeColor="#880e4f" // 선의 색깔입니다
            strokeOpacity={0.8} // 선의 불투명도입니다 0에서 1 사이값이며 0에 가까울수록 투명합니다
            strokeStyle="shortdash" // 선의 스타일입니다
            fillOpacity={0}
            zIndex={1000}
          />
        ) : null}
        {layers.map((layer: Layer) => (
          <CustomOverlayMap // 커스텀 오버레이를 표시할 Container
            // 커스텀 오버레이가 표시될 위치입니다
            position={layer.centroid}
            key={layer.id}
            zIndex={1500}
          >
            <Typography variant="body2">
              {parent.name}
              {layer.name}
            </Typography>
          </CustomOverlayMap>
        ))}
      </Map>
      <AlertDialog open={openDialog} handleClose={handleDialogClose} />

      <Grid item xs={12}>
        <IconLabelButton
          visible
          iconName="focus"
          label="Center"
          onClick={handleCenter}
        />
        <EditToggleButton
          visible={editable}
          isLock={isLock || !editable}
          onClick={handleEditToggle}
        />
        <IconLabelButton
          // disabled={!drawable}
          disabled={maxNumLayers <= layers.length}
          visible={!isLock && editable}
          iconName="drawing"
          label="새로운 구역 그려서 추가하기"
          onClick={selectOverlay(kakao.maps.drawing.OverlayType.POLYGON)}
        />
        <IconLabelButton
          // disabled={!drawable}
          visible={!isLock && editable}
          iconName="check"
          label="새로운 경계 확정"
          onClick={saveNewLayer}
        />
        {/* <IconLabelButton
          // disabled={!drawable}
          visible={!isLock && editable}
          iconName="reload"
          label="불러오기"
          onClick={getLayers}
        /> */}
      </Grid>
      <FormHelperText>{errorMessage.message}</FormHelperText>
    </>
  );
}
